import { useEffect, useState } from "react";

import { NotificationPreferences } from "../../../interfaces/UserInfo";
import { useAuth } from "../../../firebase/AuthProvider";
import { getNotificationPreferenceById, updateNotificationPreference } from "../../../services/notificationServices/NotificationService";
import { NOTIFICATION_TIME_OPTIONS } from "../../../Data/SD";

export default function NotificationDropdown() {
    const { currentUser } = useAuth()
    const userId = (currentUser as any)?.uid;
    const token = (currentUser as any)?.accessToken;

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [preferences, setPreferences] = useState({
        email: false,
        web: false,
        reminderTime: 0,
    });

    // Notification timing options
    const timingOptions = [
        { label: "5 Minutes", value: 5 },
        { label: "15 Minutes", value: 15 },
        { label: "1 Hour", value: 60 },
    ];

    // Fetch current preferences on load
    useEffect(() => {
        let isMounted = true;
        const fetchPreferences = async () => {
            if (!userId || !token) return;

            setLoading(true);
            try {
                const result = await getNotificationPreferenceById(token, userId);
                if (result && isMounted) {
                    setPreferences({
                        email: result.email,
                        web: result.web,
                        reminderTime: result.reminderTime || 0,
                    });
                }
            } catch (error) {
                console.error("Error fetching preferences:", error);
            } finally {
                setLoading(false);
            }
        };

        if (open && userId) fetchPreferences();
        return () => {
            isMounted = false;
        };
    }, [open, userId, token]);

    const handleToggle = async (type: 'email' | 'web') => {
        const updatedPreferences = {
            ...preferences,
            [type]: !preferences[type],
        };

        setPreferences(updatedPreferences);
        if (!token || !userId) return;

        setLoading(true);
        try {
            const success = await updateNotificationPreference(
                token,
                userId,
                updatedPreferences.email,
                updatedPreferences.web,
                updatedPreferences.reminderTime
            );
            if (success) console.log("Preference updated successfully");
        } catch (error) {
            console.error("Error updating preferences:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedPreferences = {
            ...preferences,
            reminderTime: Number(e.target.value),
        };

        setPreferences(updatedPreferences);
        if (!token || !userId) return;

        setLoading(true);
        try {
            const success = await updateNotificationPreference(
                token,
                userId,
                updatedPreferences.email,
                updatedPreferences.web,
                updatedPreferences.reminderTime
            );
            if (success) console.log("Reminder time updated");
        } catch (error) {
            console.error("Error updating reminder time:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative font-poppins">
            {/* Trigger Button */}
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-[10px] bg-white text-[#6B7280] shadow-dropdown border border-[#E5E7EB]"
            >
                Notification Setting
                <svg
                    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Panel */}
            {open && (
                <div
                    className="absolute mt-2 w-[260px] rounded-xl bg-white p-3 border-t border-[rgba(0,0,0,0.1)]
                    shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.1),0px_4px_6px_-1px_rgba(0,0,0,0.1)]"
                >
                    {loading && (
                        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                            <div className="w-5 h-5 border-t-2 border-blue-500 rounded-full animate-spin"></div>
                        </div>
                    )}

                    <p className="text-[#0A0A0A] font-medium leading-[20px] mb-3">
                        Notification Preferences
                    </p>

                    {/* Email Checkbox */}
                    <label className="flex items-center gap-2  text-[#0A0A0A] mb-2">
                        <input
                            checked={preferences.email}
                            type="checkbox"
                            onChange={() => handleToggle("email")}
                            disabled={loading}
                            className="w-4 h-4 accent-[#0A0A0A]"
                        />
                        Email
                    </label>

                    <div className="h-px bg-gray-300 my-3" />

                    {/* Reminder Timing */}
                    <p className=" text-[#0A0A0A] mb-2">
                        Reminder Timing :
                    </p>

                    <div className="space-y-2  text-[#0A0A0A]">
                        {NOTIFICATION_TIME_OPTIONS.map((option) => (
                            <label key={option.value} className="d-flex align-items-center gap-2 w-100">
                                <input
                                    type="radio"
                                    name="notification-timing"
                                    value={option.value}
                                    checked={preferences.reminderTime === option.value}
                                    onChange={handleChange}
                                    className="w-4 h-4"
                                />
                                <span className="text-sm">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}